import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type RegisteredDataSource } from "@/lib/forex-api";

const capabilityLabel: Record<string, string> = {
  market: "行情源",
  macro: "宏观/事件源",
  execution: "账户/执行回传源",
};

export default function DataSourcesDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceId = searchParams.get("id") || "";
  const [sources, setSources] = useState<RegisteredDataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    forexApi.listRegisteredDataSources()
      .then((sourceResult) => {
        if (cancelled) return;
        setSources(sourceResult.items);
      })
      .catch(() => {
        if (!cancelled) setError("数据源详情加载失败，请检查后端服务或登录态。");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const source = sources.find((item) => item.source_id === sourceId) || sources[0];

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "系统已注册数据源清单", href: "/forex/data-sources" }, { label: "数据源详情" }]} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">{source?.name || "数据源详情"}</h2>
        <p className="text-xs text-text-muted mt-0.5">数据源由代码注册，B 端只读查看健康状态。</p>
          <p className="text-[10px] mt-1 text-emerald-500">/quantum/business/forex/registered-data-sources</p>
        </div>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line text-sm" />返回列表
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}

      {!loading && source && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-surface-border px-3 py-3 text-center"><p className="text-[10px] text-text-muted">状态</p><p className={`text-sm font-semibold mt-1 ${source.status === "enabled" ? "text-emerald-500" : "text-red-500"}`}>{source.status === "enabled" ? "已启用" : "已停用"}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-3 text-center"><p className="text-[10px] text-text-muted">延迟</p><p className="text-lg font-semibold font-mono text-text-primary">{source.health.latency_ms}ms</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-3 text-center"><p className="text-[10px] text-text-muted">可用率</p><p className="text-lg font-semibold font-mono text-emerald-500">{source.health.available_rate}%</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-3 text-center"><p className="text-[10px] text-text-muted">执行事实源</p><p className={`text-sm font-semibold mt-1 ${source.used_for_execution ? "text-emerald-500" : "text-text-muted"}`}>{source.used_for_execution ? "是" : "否"}</p></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-surface-border p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold text-text-primary mb-3">注册信息</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  ["Source ID", source.source_id],
                  ["名称", source.name],
                  ["能力", capabilityLabel[source.capability] || source.capability],
                  ["权威等级", source.authority_level],
                  ["刷新周期", `${source.freshness_seconds}s`],
                  ["Provider", source.provider],
                ].map(([label, value]) => (
                  <div key={label} className="bg-surface/50 rounded-md p-2.5">
                    <span className="text-[10px] text-text-muted">{label}</span>
                    <p className="text-xs text-text-primary font-mono mt-0.5">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">边界说明</h3>
              <div className="space-y-2 text-xs text-text-secondary">
                <p>后台不新增、不编辑、不维护 provider credential。</p>
        <p>策略运行使用已注册的数据源能力。</p>
                <p>如需新增连接，请通过服务端代码注册和发布。</p>
              </div>
            </div>
          </div>

        </>
      )}
    </div>
  );
}
